import { useCallback, useEffect, useId, useState } from 'react';
import { Landmark, RefreshCw } from 'lucide-react';
import api from '../../../utils/api';
import { PartnerEndpoints } from '../../../utils/endpoints';
import { Permissions } from '../../../utils/permissions';
import { apiErrorMessage } from '../../../utils/apiError';
import { formatInstantLagos } from '../../../utils/datetime';
import { useAuth } from '../../../AuthContext';
import PageHeader from '../../shell/PageHeader';
import { Alert, Button, Card, DetailRow, EmptyState, FormField } from '../../ui';
import SubMerchantDeactivated, { isSubMerchantDeactivated } from './sub-merchant/SubMerchantDeactivated';

const breadcrumbs = [{ label: 'Home', to: '/layout' }, { label: 'Bank Account' }];

const ACCOUNT_NUMBER_PATTERN = /^\d{10}$/;

const validate = ({ bank_code: bankCode, account_number: accountNumber }) => {
  const errors = {};
  if (!bankCode) errors.bank_code = ['Select the bank the account is held with.'];
  if (!accountNumber) errors.account_number = ['Enter the account number.'];
  else if (!ACCOUNT_NUMBER_PATTERN.test(accountNumber)) {
    errors.account_number = ['The account number must be exactly 10 digits.'];
  }
  return errors;
};

const CurrentAccount = ({ account, loading }) =>
  loading || account?.account_number ? (
    <>
      <DetailRow label="Bank" loading={loading} value={account?.bank_name ?? account?.bank_code} />
      <DetailRow label="Account number" loading={loading} value={account?.account_number} />
      <DetailRow label="Account name" loading={loading} value={account?.account_name} />
      <DetailRow label="Verified (WAT)" loading={loading} value={formatInstantLagos(account?.verified_at)} />
    </>
  ) : (
    <EmptyState
      icon={<Landmark className="h-8 w-8" aria-hidden="true" />}
      title="No bank account set yet"
      message="Withdrawals can't be paid until you add one."
    />
  );

const BankAccountForm = ({ account, onSaved }) => {
  const fieldId = useId();
  const [banks, setBanks] = useState([]);
  const [banksError, setBanksError] = useState('');
  const [form, setForm] = useState({ bank_code: '', account_number: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get(PartnerEndpoints.ownBanks)
      .then((response) => setBanks(Array.isArray(response?.data?.data) ? response.data.data : []))
      .catch((failure) => setBanksError(apiErrorMessage(failure, 'We could not load the bank list. Please try again.')));
  }, []);

  useEffect(() => {
    setForm({ bank_code: account?.bank_code ?? '', account_number: account?.account_number ?? '' });
  }, [account]);

  const setField = (name) => (event) => setForm((current) => ({ ...current, [name]: event.target.value }));
  const fieldError = (name) => fieldErrors[name]?.[0];

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    const invalid = validate(form);
    setFieldErrors(invalid);
    if (Object.keys(invalid).length > 0) return;

    setSaving(true);
    try {
      const response = await api.put(PartnerEndpoints.ownBankAccount, {
        bank_code: form.bank_code,
        account_number: form.account_number,
      });
      onSaved(response.data.data);
    } catch (failure) {
      const errors = failure?.response?.data?.errors;
      if (errors) setFieldErrors(errors);
      else setError(apiErrorMessage(failure, 'We could not update your bank account. Please try again.'));
    }
    setSaving(false);
  };

  return (
    <Card title={account?.account_number ? 'Change bank account' : 'Add bank account'}>
      {error && (
        <Alert variant="danger" className="mb-4" onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit} noValidate className="max-w-lg">
        <FormField label="Bank" htmlFor={`${fieldId}-bank`} error={fieldError('bank_code') || banksError} required>
          <select
            id={`${fieldId}-bank`}
            className="form-select"
            value={form.bank_code}
            onChange={setField('bank_code')}
            required
          >
            <option value="">Select a bank</option>
            {banks.map((bank) => (
              <option key={bank.bankCode} value={bank.bankCode}>
                {bank.name}
              </option>
            ))}
          </select>
        </FormField>

        <FormField
          label="Account number"
          htmlFor={`${fieldId}-account`}
          error={fieldError('account_number')}
          hint="The account name is confirmed with the bank when you save."
          required
        >
          <input
            id={`${fieldId}-account`}
            className="form-control"
            inputMode="numeric"
            maxLength={10}
            value={form.account_number}
            onChange={setField('account_number')}
            required
          />
        </FormField>

        <Button type="submit" loading={saving}>
          Verify and save
        </Button>
      </form>
    </Card>
  );
};

const SubMerchantBankAccount = () => {
  const { hasPermission } = useAuth();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [savedName, setSavedName] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    api
      .get(PartnerEndpoints.ownBankAccount)
      .then((response) => setAccount(response?.data?.data ?? null))
      .catch(setLoadError)
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const handleSaved = (saved) => {
    setAccount(saved);
    setSavedName(saved?.account_name ?? '');
  };

  if (isSubMerchantDeactivated(loadError)) {
    return (
      <>
        <PageHeader title="Bank Account" breadcrumbs={breadcrumbs} />
        <SubMerchantDeactivated />
      </>
    );
  }

  return (
    <>
      <PageHeader title="Bank Account" breadcrumbs={breadcrumbs} />

      {savedName && (
        <Alert variant="success" className="mb-6" onDismiss={() => setSavedName('')}>
          Payouts will go to {savedName}.
        </Alert>
      )}

      {loadError ? (
        <Alert variant="danger" className="mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>{apiErrorMessage(loadError, 'We could not load your bank account. Please try again.')}</span>
            <Button variant="outline-danger" size="sm" onClick={load}>
              <RefreshCw className="w-4 h-4" aria-hidden="true" />
              Retry
            </Button>
          </div>
        </Alert>
      ) : (
        <Card title="Payout account">
          <CurrentAccount account={account} loading={loading} />
        </Card>
      )}

      {hasPermission(Permissions.subMerchantBankAccountUpdate) && !loading && !loadError && (
        <BankAccountForm account={account} onSaved={handleSaved} />
      )}
    </>
  );
};

export default SubMerchantBankAccount;
